import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Autocomplete,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  useFeedStock,
  useFeedTypes,
  useFeedBrands,
  useProcureFeed,
  useUpdateStockThreshold,
  useUpdateStockQuantity,
} from '../hooks/useFeed';
import type { FeedStock } from '../types';

const FeedStockLevelsPage: React.FC = () => {
  const [openProcureDialog, setOpenProcureDialog] = useState(false);
  const [openThresholdDialog, setOpenThresholdDialog] = useState(false);
  const [openQuantityDialog, setOpenQuantityDialog] = useState(false);
  const [selectedStock, setSelectedStock] = useState<FeedStock | null>(null);
  const [selectedFeedType, setSelectedFeedType] = useState<string | null>(null);
  const [selectedFeedBrand, setSelectedFeedBrand] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning',
  });

  const thresholdMutation = useUpdateStockThreshold();
  const quantityMutation = useUpdateStockQuantity();

  const [procureForm, setProcureForm] = useState({
    feed_type_id: '',
    feed_brand_id: '',
    bags_purchased: '',
    cost_per_bag: '',
    procurement_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    notes: '',
  });

  const [thresholdForm, setThresholdForm] = useState({ threshold_kg: '' });
  const [quantityForm, setQuantityForm] = useState({ quantity_kg: '', reason: '' });

  const { data: stock, isLoading, error, refetch, isFetching: stockFetching } = useFeedStock();
  const { data: feedTypes, isFetching: feedTypesFetching } = useFeedTypes();
  const { data: feedBrands, isFetching: feedBrandsFetching } = useFeedBrands();
  const procureMutation = useProcureFeed();

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Feed Stock Data
            </Typography>
            <Typography variant="body2" paragraph>
              {error instanceof Error
                ? error.message
                : 'An error occurred while fetching feed stock levels. Please try again.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Filter stock data
  const filteredStock =
    stock?.filter((item) => {
      const matchesFeedType = !selectedFeedType || item.feed_type === selectedFeedType;
      const matchesFeedBrand = !selectedFeedBrand || item.feed_brand === selectedFeedBrand;
      return matchesFeedType && matchesFeedBrand;
    }) || [];

  const handleProcure = async () => {
    if (
      !procureForm.feed_type_id ||
      !procureForm.feed_brand_id ||
      !procureForm.bags_purchased ||
      !procureForm.cost_per_bag ||
      !procureForm.procurement_date
    ) {
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }

    try {
      await procureMutation.mutateAsync({
        feed_type_id: parseInt(procureForm.feed_type_id),
        feed_brand_id: parseInt(procureForm.feed_brand_id),
        bags_purchased: procureForm.bags_purchased,
        cost_per_bag: procureForm.cost_per_bag,
        procurement_date: procureForm.procurement_date,
        delivery_date: procureForm.delivery_date || undefined,
        notes: procureForm.notes || undefined,
      });
      showSnackbar('Feed procurement recorded successfully');
      setOpenProcureDialog(false);
      setProcureForm({
        feed_type_id: '',
        feed_brand_id: '',
        bags_purchased: '',
        cost_per_bag: '',
        procurement_date: new Date().toISOString().split('T')[0],
        delivery_date: '',
        notes: '',
      });
    } catch (error) {
      showSnackbar('Failed to record procurement', 'error');
    }
  };

  const handleUpdateThreshold = async () => {
    if (!selectedStock || !thresholdForm.threshold_kg) return;
    try {
      await thresholdMutation.mutateAsync({
        stockId: selectedStock.id,
        threshold: parseFloat(thresholdForm.threshold_kg),
      });
      showSnackbar('Threshold updated successfully');
      setOpenThresholdDialog(false);
      setSelectedStock(null);
    } catch (error) {
      showSnackbar('Failed to update threshold', 'error');
    }
  };

  const handleUpdateQuantity = async () => {
    if (!selectedStock || !quantityForm.quantity_kg) return;
    try {
      await quantityMutation.mutateAsync({
        stockId: selectedStock.id,
        quantityKg: parseFloat(quantityForm.quantity_kg),
        reason: quantityForm.reason || undefined,
      });
      showSnackbar('Stock quantity updated successfully');
      setOpenQuantityDialog(false);
      setSelectedStock(null);
      setQuantityForm({ quantity_kg: '', reason: '' });
    } catch (error) {
      showSnackbar('Failed to update stock quantity', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {(stockFetching || feedTypesFetching || feedBrandsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}
      {/* Content */}
      <Paper sx={{ p: 3, border: '1px solid rgba(76, 175, 80, 0.3)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Current Stock
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={stockFetching}
              sx={{
                borderColor: '#66bb6a',
                color: '#66bb6a',
                '&:hover': {
                  borderColor: '#4caf50',
                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                },
              }}
            >
              {stockFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenProcureDialog(true)}
              sx={{
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                color: '#ffffff',
              }}
            >
              Procure Feed
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Autocomplete
            options={Array.from(new Set(stock?.map((s) => s.feed_type) || []))}
            value={selectedFeedType}
            onChange={(_, newValue) => setSelectedFeedType(newValue)}
            renderInput={(params) => <TextField {...params} label="Filter by Feed Type" />}
            sx={{ flex: 1 }}
          />
          <Autocomplete
            options={Array.from(new Set(stock?.map((s) => s.feed_brand) || []))}
            value={selectedFeedBrand}
            onChange={(_, newValue) => setSelectedFeedBrand(newValue)}
            renderInput={(params) => <TextField {...params} label="Filter by Brand" />}
            sx={{ flex: 1 }}
          />
          {(selectedFeedType || selectedFeedBrand) && (
            <Button
              onClick={() => {
                setSelectedFeedType(null);
                setSelectedFeedBrand(null);
              }}
              sx={{ color: '#ff9800' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={filteredStock}
            columns={[
              {
                field: 'feed_type',
                headerName: 'Feed Type',
                flex: 1,
                minWidth: 120,
                headerClassName: 'data-grid-header',
              },
              {
                field: 'feed_brand',
                headerName: 'Brand',
                flex: 0.8,
                minWidth: 100,
                headerClassName: 'data-grid-header',
              },
              {
                field: 'stock_summary',
                headerName: 'Stock (Bags)',
                flex: 2.5,
                minWidth: 200,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => {
                  const totalBags = parseFloat(params.row.current_stock_bags);
                  const availableKg = parseFloat(params.row.available_stock_kg);
                  const reservedKg = parseFloat(params.row.reserved_stock_kg);
                  const kgPerBag = 50;
                  const availableBags = availableKg / kgPerBag;
                  const reservedBags = reservedKg / kgPerBag;

                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: params.row.is_low_stock ? '#f44336' : '#66bb6a',
                            fontSize: '1.25rem',
                          }}
                        >
                          {totalBags.toFixed(1)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          bags total
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#66bb6a', fontWeight: 600 }}>
                          ✓ {availableBags.toFixed(1)} avail
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ffa726', fontWeight: 600 }}>
                          ⊙ {reservedBags.toFixed(1)} reserved
                        </Typography>
                      </Box>
                    </Box>
                  );
                },
              },
              {
                field: 'available_stock_kg',
                headerName: 'Available (kg)',
                flex: 1,
                type: 'number',
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                    {parseFloat(params.value).toFixed(0)} kg
                  </Typography>
                ),
              },
              {
                field: 'average_cost_per_bag',
                headerName: 'Avg Cost',
                flex: 1,
                type: 'number',
                headerAlign: 'center',
                align: 'center',
                valueFormatter: (value) =>
                  isNaN(parseFloat(value)) ? '-' : `₵${parseFloat(value).toFixed(0)}`,
              },
              {
                field: 'last_activity_date',
                headerName: 'Last Activity',
                flex: 1.5,
                minWidth: 150,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => {
                  if (!params.row.last_activity_date) {
                    return (
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
                        No activity
                      </Typography>
                    );
                  }

                  const activityType = params.row.last_activity_type;
                  const daysAgo = params.row.days_since_last_activity;
                  const activityColor = activityType === 'procurement' ? '#42a5f5' : '#ab47bc';
                  const activityLabel = activityType === 'procurement' ? 'Procured' : 'Produced';

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        py: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: `${activityColor}15`,
                          border: `1px solid ${activityColor}40`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: activityColor,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {activityLabel}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        {daysAgo === 0 ? 'Today' : `${daysAgo}d ago`}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.4)',
                          fontSize: '0.65rem',
                        }}
                      >
                        {new Date(params.row.last_activity_date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </Typography>
                    </Box>
                  );
                },
              },
              {
                field: 'is_low_stock',
                headerName: 'Status',
                flex: 0.6,
                minWidth: 80,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                  params.value ? (
                    <Alert severity="error" sx={{ py: 0, px: 1 }}>
                      Low
                    </Alert>
                  ) : (
                    <Alert severity="success" sx={{ py: 0, px: 1 }}>
                      OK
                    </Alert>
                  ),
              },
              {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                flex: 1,
                getActions: (params) => [
                  <GridActionsCellItem
                    key="edit-quantity"
                    icon={<EditIcon />}
                    label="Update Quantity"
                    onClick={() => {
                      setSelectedStock(params.row);
                      setQuantityForm({
                        quantity_kg: params.row.current_stock_kg.toString(),
                        reason: '',
                      });
                      setOpenQuantityDialog(true);
                    }}
                    showInMenu
                  />,
                  <GridActionsCellItem
                    key="edit-threshold"
                    icon={<EditIcon />}
                    label="Edit Threshold"
                    onClick={() => {
                      setSelectedStock(params.row);
                      setThresholdForm({
                        threshold_kg: params.row.minimum_threshold_kg.toString(),
                      });
                      setOpenThresholdDialog(true);
                    }}
                    showInMenu
                  />,
                ],
              },
            ]}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            loading={isLoading}
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'rgba(76, 175, 80, 0.15)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: '#ffffff',
                fontWeight: 600,
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Procure Feed Dialog */}
      <Dialog
        open={openProcureDialog}
        onClose={() => setOpenProcureDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(76, 175, 80, 0.3)' } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(76, 175, 80, 0.3)', color: '#66bb6a' }}>
          Procure Feed
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={feedTypes || []}
              getOptionLabel={(option) => option.name}
              value={feedTypes?.find((t) => t.id.toString() === procureForm.feed_type_id) || null}
              onChange={(_, newValue) =>
                setProcureForm({ ...procureForm, feed_type_id: newValue?.id.toString() || '' })
              }
              renderInput={(params) => <TextField {...params} label="Feed Type" required />}
            />

            <Autocomplete
              options={feedBrands || []}
              getOptionLabel={(option) => option.name}
              value={feedBrands?.find((b) => b.id.toString() === procureForm.feed_brand_id) || null}
              onChange={(_, newValue) =>
                setProcureForm({ ...procureForm, feed_brand_id: newValue?.id.toString() || '' })
              }
              renderInput={(params) => <TextField {...params} label="Feed Brand" required />}
            />

            <TextField
              label="Bags Purchased"
              type="number"
              required
              value={procureForm.bags_purchased}
              onChange={(e) => setProcureForm({ ...procureForm, bags_purchased: e.target.value })}
              slotProps={{ htmlInput: { min: 0.1, step: 0.1 } }}
              helperText="Minimum 0.1 bags"
            />

            <TextField
              label="Cost Per Bag (GH₵)"
              type="number"
              required
              value={procureForm.cost_per_bag}
              onChange={(e) => setProcureForm({ ...procureForm, cost_per_bag: e.target.value })}
              slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
              helperText="Minimum GH₵ 0.01"
            />

            <TextField
              label="Procurement Date"
              type="date"
              required
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { max: new Date().toISOString().split('T')[0] },
              }}
              value={procureForm.procurement_date}
              onChange={(e) => setProcureForm({ ...procureForm, procurement_date: e.target.value })}
              helperText="Date must be today or earlier"
            />

            <TextField
              label="Delivery Date (Optional)"
              type="date"
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: procureForm.procurement_date },
              }}
              value={procureForm.delivery_date}
              onChange={(e) => setProcureForm({ ...procureForm, delivery_date: e.target.value })}
              helperText="Must be on or after procurement date"
            />

            <TextField
              label="Notes (Optional)"
              multiline
              rows={3}
              value={procureForm.notes}
              onChange={(e) => setProcureForm({ ...procureForm, notes: e.target.value })}
              slotProps={{ htmlInput: { maxLength: 1000 } }}
              helperText={`${procureForm.notes.length}/1000 characters`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenProcureDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProcure}
            disabled={procureMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#ffffff',
            }}
          >
            {procureMutation.isPending ? 'Processing...' : 'Procure'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Stock Quantity Dialog */}
      <Dialog
        open={openQuantityDialog}
        onClose={() => setOpenQuantityDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(76, 175, 80, 0.3)' } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(76, 175, 80, 0.3)', color: '#66bb6a' }}>
          Update Stock Quantity
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedStock && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Current Stock: {parseFloat(selectedStock.current_stock_kg).toFixed(2)} kg (
                {parseFloat(selectedStock.current_stock_bags).toFixed(2)} bags)
              </Alert>
            )}
            <TextField
              label="New Quantity (kg)"
              type="number"
              fullWidth
              required
              value={quantityForm.quantity_kg}
              onChange={(e) => setQuantityForm({ ...quantityForm, quantity_kg: e.target.value })}
              slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
              helperText="Enter the new total stock quantity in kilograms"
            />
            <TextField
              label="Reason (Optional)"
              multiline
              rows={3}
              fullWidth
              value={quantityForm.reason}
              onChange={(e) => setQuantityForm({ ...quantityForm, reason: e.target.value })}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              helperText={`${quantityForm.reason.length}/500 characters - Explain why the stock is being adjusted`}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(76, 175, 80, 0.3)',
            backgroundColor: 'rgba(76, 175, 80, 0.05)',
          }}
        >
          <Button
            onClick={() => setOpenQuantityDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateQuantity}
            disabled={quantityMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#ffffff',
            }}
          >
            {quantityMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Threshold Dialog */}
      <Dialog
        open={openThresholdDialog}
        onClose={() => setOpenThresholdDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(76, 175, 80, 0.3)' } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(76, 175, 80, 0.3)', color: '#66bb6a' }}>
          Update Stock Threshold
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Threshold (kg)"
              type="number"
              fullWidth
              required
              value={thresholdForm.threshold_kg}
              onChange={(e) => setThresholdForm({ threshold_kg: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(76, 175, 80, 0.3)',
            backgroundColor: 'rgba(76, 175, 80, 0.05)',
          }}
        >
          <Button
            onClick={() => setOpenThresholdDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateThreshold}
            disabled={thresholdMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#ffffff',
            }}
          >
            {thresholdMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedStockLevelsPage;
